import React, { useState, useEffect } from 'react';
import { Card, Form, Switch, InputNumber, Button, Row, Col, Alert, Spin, Space, Tooltip, Divider, Badge } from 'antd';
import { InfoCircleOutlined, SaveOutlined, ReloadOutlined, SafetyOutlined, UserOutlined, CalendarOutlined } from '@ant-design/icons';
import { customBaseUrl, httpGet, httpPut } from '../../store/http';
import { NotificationManager } from 'react-notifications';
import Swal from 'sweetalert2';
import moment from 'moment';

const SMSOtpGateSettings = () => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [lastUpdatedBy, setLastUpdatedBy] = useState('');
  const [lastUpdatedAt, setLastUpdatedAt] = useState('');
  const [form] = Form.useForm();

  const fetchOtpConfig = async () => {
    setLoading(true);
    try {
      const res = await httpGet(`${customBaseUrl.terminal}/api/v1/admin/config/otp`);
      if (res?.success || res?.status) {
        const config = res.data || res;
        form.setFieldsValue({
          otpEnabled: config.otpEnabled ?? config.enabled ?? false,
          otpThresholdAmount: config.otpThresholdAmount ?? config.threshold ?? 0,
          otpTtlSeconds: config.otpTtlSeconds ?? config.ttl ?? config.ttlSeconds ?? config.ttlInMinutes ?? 300,
          otpMaxAttempts: config.otpMaxAttempts ?? config.maxAttempts ?? config.maxVerificationAttempts ?? 3,
        });
        setLastUpdatedBy(config.updatedBy || '');
        setLastUpdatedAt(config.updatedAt || '');
      } else {
        NotificationManager.error(res?.message || "Failed to load SMS-OTP config.");
      }
    } catch (err) {
      console.error(err);
      NotificationManager.error("An error occurred while loading OTP configuration.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOtpConfig();
  }, []);

  const handleSave = async (values) => {
    setSaving(true);
    try {
      const payload = {
        otpEnabled: !!values.otpEnabled,
        otpThresholdAmount: Number(values.otpThresholdAmount || 0),
        otpTtlSeconds: Number(values.otpTtlSeconds || 300),
        otpMaxAttempts: Number(values.otpMaxAttempts || 3),
      };

      const res = await httpPut(`${customBaseUrl.terminal}/api/v1/admin/config/otp`, payload);
      if (res?.success || res?.status || res?.status === "SUCCESS") {
        Swal.fire({
          title: "Configuration Saved",
          text: "SMS-OTP gate configuration has been updated successfully.",
          icon: "success",
          confirmButtonColor: "#ff6700"
        });
        fetchOtpConfig();
      } else {
        NotificationManager.error(res?.message || "Failed to save configuration.");
      }
    } catch (err) {
      console.error(err);
      NotificationManager.error("An error occurred while saving configuration.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Spin spinning={loading} tip="Loading configurations...">
      <div style={{ padding: '8px 0' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, padding: '12px 16px', background: '#fafafa', border: '1px solid #f0f0f0', borderRadius: 8 }}>
          <span style={{ color: '#595959', fontSize: '13px' }}>
            Configure the threshold limits, TTL, and max attempts for transactional SMS-OTP gates.
          </span>
          <Button size="small" type="default" icon={<ReloadOutlined />} onClick={fetchOtpConfig}>
            Reload Config
          </Button>
        </div>

        <Card
          title={
            <Space>
              <SafetyOutlined style={{ color: '#ff6700' }} />
              <span>SMS-OTP Transaction Gate Configuration</span>
            </Space>
          }
          style={{ borderRadius: 12, boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)', border: '1px solid #f0f0f0' }}
        >
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSave}
            requiredMark={false}
          >
            <Row gutter={[24, 24]}>
              <Col xs={24} md={12}>
                <Form.Item
                  name="otpEnabled"
                  label={
                    <Space>
                      <strong>Enable SMS-OTP Gate</strong>
                      <Tooltip title="When enabled, transactions exceeding the threshold amount will require SMS-OTP verification.">
                        <InfoCircleOutlined style={{ color: '#bfbfbf' }} />
                      </Tooltip>
                    </Space>
                  }
                  valuePropName="checked"
                >
                  <Switch checkedChildren="ON" unCheckedChildren="OFF" />
                </Form.Item>
              </Col>

              <Col xs={24} md={12}>
                <Form.Item
                  name="otpThresholdAmount"
                  label={
                    <Space>
                      <strong>Threshold Amount (NGN)</strong>
                      <Tooltip title="Minimum transaction amount to trigger SMS-OTP verification.">
                        <InfoCircleOutlined style={{ color: '#bfbfbf' }} />
                      </Tooltip>
                    </Space>
                  }
                  rules={[{ required: true, message: 'Please enter threshold amount' }]}
                >
                  <InputNumber
                    style={{ width: '100%' }}
                    min={0}
                    placeholder="e.g. 50000"
                    formatter={value => `₦ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                    parser={value => value.replace(/\₦\s?|(,*)/g, '')}
                  />
                </Form.Item>
              </Col>

              <Col xs={24} md={12}>
                <Form.Item
                  name="otpTtlSeconds"
                  label={
                    <Space>
                      <strong>OTP Time-to-Live (Seconds)</strong>
                      <Tooltip title="Validity duration of the generated OTP in seconds.">
                        <InfoCircleOutlined style={{ color: '#bfbfbf' }} />
                      </Tooltip>
                    </Space>
                  }
                  rules={[{ required: true, message: 'Please enter OTP TTL' }]}
                >
                  <InputNumber style={{ width: '100%' }} min={30} placeholder="e.g. 300 (5 minutes)" />
                </Form.Item>
              </Col>

              <Col xs={24} md={12}>
                <Form.Item
                  name="otpMaxAttempts"
                  label={
                    <Space>
                      <strong>Max Verification Attempts</strong>
                      <Tooltip title="Maximum allowed incorrect attempts before the OTP is invalidated.">
                        <InfoCircleOutlined style={{ color: '#bfbfbf' }} />
                      </Tooltip>
                    </Space>
                  }
                  rules={[{ required: true, message: 'Please enter max attempts' }]}
                >
                  <InputNumber style={{ width: '100%' }} min={1} max={10} placeholder="e.g. 3" />
                </Form.Item>
              </Col>
            </Row>

            <Divider style={{ margin: '24px 0 16px 0' }} />

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              {(lastUpdatedBy || lastUpdatedAt) ? (
                <div style={{ display: 'flex', gap: 16, color: '#8c8c8c', fontSize: '12px', alignItems: 'center' }}>
                  {lastUpdatedBy && (
                    <span>
                      <UserOutlined style={{ marginRight: 4 }} />
                      Last updated by: <strong>{lastUpdatedBy}</strong>
                    </span>
                  )}
                  {lastUpdatedAt && (
                    <span>
                      <CalendarOutlined style={{ marginRight: 4 }} />
                      Updated at: <strong>{moment(lastUpdatedAt).format('YYYY-MM-DD HH:mm:ss')}</strong>
                    </span>
                  )}
                </div>
              ) : <div />}
              <Button
                type="primary"
                htmlType="submit"
                icon={<SaveOutlined />}
                loading={saving}
                style={{
                  background: '#ff6700',
                  borderColor: '#ff6700',
                  height: 40,
                  borderRadius: 6,
                  fontWeight: 600,
                  boxShadow: '0 4px 10px rgba(255, 103, 0, 0.2)'
                }}
              >
                Save Configuration
              </Button>
            </div>
          </Form>
        </Card>
      </div>
    </Spin>
  );
};

export default SMSOtpGateSettings;
